import {Component, computed, inject, input, OnInit, signal} from '@angular/core';
import {TranslocoDirective} from '@jsverse/transloco';
import {
  ArrowLeftIcon,
  CalendarIcon,
  CheckIcon,
  CoinsIcon,
  CopyIcon,
  EarthIcon,
  ExternalLinkIcon,
  GraduationCapIcon,
  InfoIcon,
  LinkIcon,
  LucideAngularModule,
  MegaphoneOffIcon,
  MessageSquareMoreIcon,
  MessagesSquareIcon,
  MicIcon,
  MilestoneIcon,
  PiggyBankIcon,
  ScanEyeIcon,
  ScanFaceIcon,
  SendIcon,
  Share2Icon,
  SparklesIcon,
  SquarePenIcon,
  SquareUserRoundIcon,
  TrafficConeIcon,
  Trash2Icon,
  TriangleAlertIcon
} from 'lucide-angular';
import {Router} from '@angular/router';
import {Meta, Title} from '@angular/platform-browser';
import {DOCUMENT} from '@angular/common';
import {take} from 'rxjs';
import {RoutingService} from '@core/service/routing.service';
import {ScrollManagerService} from '@core/service/scroll-manager.service';
import {HostDTO} from '@domain/host/host.dto';
import {ScrollTargetDirective} from '@domain/common/directive/scroll-target.directive';
import {HostContactForm} from '@domain/host/host-contact-form/host-contact-form.component';
import {LegalFormPipe} from '@domain/common/pipe/legal-form.pipe';
import {ActivitiesPreviewComponent} from '@domain/activity/component/activities-preview/activities-preview.component';
import {ActivityTypeIconComponent} from '@domain/activity/component/activity-type-icon/activity-type-icon.component';
import {HostContactNote} from '@domain/host/host-contact-note/host-contact-note.component';
import {ActivityDTO, ActivityType} from '@domain/activity/activity.dto';
import {HostApi} from '@domain/host/host.api';
import {NotificationService} from '@shared/notification/notification.service';
import {HostUtil} from '@domain/host/host.util';
import {RouteGenerator} from '@core/route-generator';
import {UI_ROUTES} from '@core/ui.routes';
import {HostEditorComponent} from '@domain/host/component/form/host-editor/host-editor.component';
import {CoverViewerComponent} from '@shared/form/media/cover-uploader/cover-viewer/cover-viewer.component';

@Component({
  selector: 'app-host-details',
  providers: [],
  imports: [
    TranslocoDirective,
    LucideAngularModule,
    ScrollTargetDirective,
    HostContactForm,
    LegalFormPipe,
    ActivitiesPreviewComponent,
    ActivityTypeIconComponent,
    HostContactNote,
    CoverViewerComponent
  ],
  templateUrl: './host-details.component.html',
  styleUrl: './host-details.component.scss'
})
export class HostDetailsComponent implements OnInit {

  public readonly isPreview = input.required<boolean>();
  public readonly isPublic = input.required<boolean>();

  public readonly SECTION_SEND_MESSAGE = 'section-message';

  public readonly activityTypes = ActivityType;

  public readonly icons = {
    info: InfoIcon,
    map: MilestoneIcon,
    calendar: CalendarIcon,
    audience: ScanFaceIcon,
    coins: CoinsIcon,
    free: PiggyBankIcon,
    check: CheckIcon,
    warn: TriangleAlertIcon,
    wip: TrafficConeIcon,
    links: LinkIcon,
    web: EarthIcon,
    back: ArrowLeftIcon,
    edit: SquarePenIcon,
    delete: Trash2Icon,
    copy: CopyIcon,
    preview: ScanEyeIcon,
    submit: SendIcon,
    unpublish: MegaphoneOffIcon,
    share: Share2Icon,
    contact: MessagesSquareIcon,
    message: MessageSquareMoreIcon,
    age: SquareUserRoundIcon,
    supervisor: GraduationCapIcon,
    externalLink: ExternalLinkIcon,
    publish: MicIcon,
    improve: SparklesIcon
  };

  private readonly router = inject(Router);
  private readonly hostApi = inject(HostApi);
  private readonly notifyService = inject(NotificationService);
  private readonly routingService = inject(RoutingService);
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);
  private readonly scrollManager = inject(ScrollManagerService);
  public readonly host = input.required<HostDTO | null>();
  public readonly allActivities = input.required<ActivityDTO[]>();
  public readonly activities = computed(() => (this.allActivities() ?? []).filter(a => a.type === ActivityType.Activity));
  public readonly events = computed(() => (this.allActivities() ?? []).filter(a => a.type === ActivityType.Event));
  public readonly hasNoActivities = computed(() => (this.activities() ?? []).length === 0);
  public readonly activity = signal<ActivityDTO | null>(
    null
  );
  public readonly h = signal<HostDTO | null>(null);

  public readonly improveMessages = computed(() => HostUtil.getImproveMessages(this.h()));

  public readonly hostName = computed(() => this.h()?.name ?? '');

  public readonly hasContactInfo = computed(() => HostUtil.hasContactInfo(this.h()));

  public showContact = signal(false);

  public readonly routes = {
    home: RouteGenerator.abs(UI_ROUTES.public.dashboard),
    search: RouteGenerator.abs(UI_ROUTES.public.activity.search)
  };

  public coverUrl: string | null = null;
  public coverThumbnailUrl: string | null = null;

  public ngOnInit(): void {
    const host = this.host();
    this.h.set(host);
    if (!host) {
      this.notifyService.toast({
        message: `Cette organisation n'existe pas ou n'est plus publiée.`
      });
      this.routingService.navigate([this.routes.home]).then(_ => {});
      return;
    }
    this.coverUrl = this.hostApi.getCoverUrl(host.id);
    this.coverThumbnailUrl = this.hostApi.getCoverThumbnailUrl(host.id);
    let seoTitle = host.name ?? '';
    this.title.setTitle(seoTitle);
    this.meta.updateTag({ name: 'description', content: host.description ?? '' });
    this.meta.updateTag({ property: 'og:title', content: seoTitle });
    this.meta.updateTag({ property: 'og:description', content: host.description ?? '' });
    if (host.cover) {
      this.meta.updateTag({ property: 'og:image', content: this.coverUrl });
    }
    const canonicalUrl = this.getUrl();
    if (!canonicalUrl) {
      return;
    }
    this.setCanonicalURL(canonicalUrl);
    this.meta.updateTag({ property: 'og:url', content: canonicalUrl});
    this.meta.updateTag({ property: 'og:type', content: 'article' });
  }

  private setCanonicalURL(url: string) {
    const link: HTMLLinkElement = this.document.querySelector("link[rel='canonical']") ?? this.document.createElement('link');
    link.setAttribute('rel', 'canonical');
    link.setAttribute('href', url);
    this.document.head.appendChild(link);
  }

  public createNewActivity(): void {
    const host = this.h();
    if (!host) {
      return;
    }
    this.hostApi.createActivity(host).pipe(
      take(1)
    ).subscribe(activity => {
      if (activity) {
        this.router.navigate([RouteGenerator.abs(UI_ROUTES.account.activity.home, {activityId: activity.id})])
          .then(_ => {});
      } else {
        this.router.navigate([RouteGenerator.abs(UI_ROUTES.account.dashboard)])
          .then(_ => {});
      }
    });
  }

  public navigateToEditHost(tab?: string): void {
    const host = this.h();
    if (!host) {
      return;
    }
    const navigationExtras = tab ? {queryParams: { [HostEditorComponent.QUERY_PARAM_TAB]: tab}} : undefined;
    this.router.navigate(
      [RouteGenerator.abs(UI_ROUTES.account.host.edit, {hostId: host.id})],
      navigationExtras
    ).then(_ => {});
  }

  public navigateToEditContact(): void {
    this.navigateToEditHost('contact');
  }

  public toggleShowContact(): void {
    this.showContact.update(val => !val);
    if (this.showContact()) {
      this.scrollManager.scroll(this.SECTION_SEND_MESSAGE);
    }
  }

  private getUrl(): string | null {
    const slug = HostUtil.getSlug(this.h());
    if (!slug) {
      return null;
    }
    return RouteGenerator.uiUrl(UI_ROUTES.public.host.home, {slug: slug});
  }

}
